import { useEffect, useState } from 'react';
import type { PlatformFlags } from '@shared/types';
import { api } from './api';

/**
 * 平台开关（前端只读）。
 *
 * 登录页与注册页在**未登录**时就要知道「现在能不能注册 / 能不能登录」，
 * 所以这里不能复用 authStore（它在未登录时是 anonymous，拿不到会话里的任何东西）。
 *
 * 拉取失败一律按「全部开放」处理：开关服务抖动不应该让用户连登录框都点不动，
 * 真正的拦截在后端（返回 403），前端这里只是提前把按钮置灰、给出提示。
 */
export const OPEN_FLAGS: PlatformFlags = {
  registrationEnabled: true,
  loginEnabled: true,
};

let cached: PlatformFlags | null = null;
let inflight: Promise<PlatformFlags> | null = null;

async function loadFlags(): Promise<PlatformFlags> {
  if (cached !== null) return cached;
  if (inflight !== null) return inflight;

  inflight = api
    .get<PlatformFlags>('/api/auth/flags')
    .then((flags) => {
      cached = flags;
      return flags;
    })
    .catch(() => OPEN_FLAGS)
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/** 页面里用的 hook：首次挂载拉一次，页内不重复请求。 */
export function usePlatformFlags(): { flags: PlatformFlags; loading: boolean } {
  const [flags, setFlags] = useState<PlatformFlags>(cached ?? OPEN_FLAGS);
  const [loading, setLoading] = useState(cached === null);

  useEffect(() => {
    let cancelled = false;
    if (cached !== null) {
      setFlags(cached);
      setLoading(false);
      return;
    }
    void loadFlags().then((value) => {
      if (cancelled) return;
      setFlags(value);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { flags, loading };
}

/** 测试或登出后需要重新读取时调用。 */
export function resetPlatformFlagsCache(): void {
  cached = null;
  inflight = null;
}
